<template>
  <v-row>
    <v-col cols="12">
      <v-card class="seller-management-card">
        <v-card-title class="d-flex align-center">
          Seller Management
          <v-spacer />
          <v-btn icon color="primary" @click="refreshAll">
            <font-awesome-icon icon="fa-solid fa-sync" />
          </v-btn>
        </v-card-title>

        <v-tabs v-model="tab" background-color="transparent" show-arrows>
          <v-tab>Sellers</v-tab>
          <v-tab>Commission</v-tab>
          <v-tab>History</v-tab>
        </v-tabs>

        <v-divider />

        <v-tabs-items v-model="tab">
          <!-- Sellers Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row class="mb-4">
                  <v-col cols="12" md="3">
                    <div class="summary-card">
                      <div class="summary-label">Total Sellers</div>
                      <div class="summary-value">{{ sellerTotals.count }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-card">
                      <div class="summary-label">Active Sellers</div>
                      <div class="summary-value">{{ sellerTotals.active }}</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-card">
                      <div class="summary-label">Total Balance</div>
                      <div class="summary-value">{{ sellerTotals.balance }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="3">
                    <div class="summary-card">
                      <div class="summary-label">Total Earned</div>
                      <div class="summary-value">{{ sellerTotals.earned }} XAF</div>
                    </div>
                  </v-col>
                </v-row>

                <div class="d-flex align-center mb-3">
                  <v-text-field
                    v-model="sellerSearch"
                    append-icon="mdi-magnify"
                    label="Search sellers"
                    single-line
                    hide-details
                    dense
                    class="mr-4"
                  />
                  <v-spacer />
                  <v-btn small color="primary" :loading="sellersLoading" @click="loadSellers">
                    Refresh
                  </v-btn>
                </div>

                <v-data-table
                  :headers="sellerHeaders"
                  :items="sellers"
                  :search="sellerSearch"
                  :loading="sellersLoading"
                  :items-per-page="15"
                  class="table-rounded"
                >
                  <template #[`item.is_active`]='{ item }'>
                    <v-chip small :color="item.is_active ? 'success' : 'grey'" dark>
                      {{ item.is_active ? 'Active' : 'Disabled' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Commission Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-alert dense outlined type="info" class="mb-4">
                  Commission is global. Choose either percentage or fixed amount.
                  It is deducted from product price (excluding delivery).
                </v-alert>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4">
                      <div class="text-subtitle-1 font-weight-bold mb-3">Current Commission</div>
                      <div class="text-body-2 mb-2">Type: <strong>{{ commissionType }}</strong></div>
                      <div class="text-body-2 mb-2">Value: <strong>{{ commissionValue }} {{ commissionType === 'PERCENT' ? '%' : 'XAF' }}</strong></div>
                      <div class="text-caption grey--text">
                        Updated: {{ commissionUpdatedAt || 'Not set' }}
                      </div>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4">
                      <div class="text-subtitle-1 font-weight-bold mb-3">Update Commission</div>

                      <v-radio-group v-model="commissionType" row class="mb-2">
                        <v-radio label="Percent" value="PERCENT" />
                        <v-radio label="Fixed (XAF)" value="FIXED" />
                      </v-radio-group>

                      <v-text-field
                        v-model="commissionValue"
                        type="number"
                        label="Commission Value"
                        placeholder="0"
                        outlined
                        dense
                      />

                      <v-text-field
                        v-model="commissionExamplePrice"
                        type="number"
                        label="Example Price (XAF)"
                        placeholder="10000"
                        outlined
                        dense
                        class="mt-2"
                      />

                      <div class="text-caption grey--text mt-2">
                        Example: commission {{ commissionExampleCommission }} XAF, seller receives {{ commissionExampleNet }} XAF.
                      </div>

                      <div class="d-flex align-center mt-4">
                        <v-btn color="primary" :loading="commissionSaving" @click="saveCommissionConfig">
                          Save
                        </v-btn>
                        <v-spacer />
                        <v-btn text small :loading="commissionLoading" @click="loadCommissionConfig">
                          Reload
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- History Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-row class="mb-2">
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="historyFilters.seller_id"
                      label="Seller ID"
                      placeholder="e.g. 12"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="historyFilters.product_id"
                      label="Product ID"
                      placeholder="e.g. 45"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="historyFilters.start_date"
                      label="Start Date (YYYY-MM-DD)"
                      dense
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="historyFilters.end_date"
                      label="End Date (YYYY-MM-DD)"
                      dense
                      outlined
                    />
                  </v-col>
                </v-row>

                <div class="d-flex align-center mb-3">
                  <v-text-field
                    v-model="historySearch"
                    append-icon="mdi-magnify"
                    label="Search history"
                    single-line
                    hide-details
                    dense
                    class="mr-4"
                  />
                  <v-spacer />
                  <v-btn small color="primary" :loading="historyLoading" @click="loadHistory">
                    Filter
                  </v-btn>
                </div>

                <v-row class="mb-3">
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Base Amount</div>
                      <div class="summary-value">{{ historyTotals.base_amount }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Commission</div>
                      <div class="summary-value">{{ historyTotals.commission_amount }} XAF</div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="summary-card">
                      <div class="summary-label">Total Seller Net</div>
                      <div class="summary-value">{{ historyTotals.seller_amount }} XAF</div>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="historyHeaders"
                  :items="history"
                  :search="historySearch"
                  :loading="historyLoading"
                  :items-per-page="15"
                  class="table-rounded"
                >
                  <template #[`item.source`]='{ item }'>
                    <v-chip x-small :color="sourceColor(item.source)" dark>
                      {{ sourceLabel(item.source) }}
                    </v-chip>
                  </template>
                  <template #[`item.commission_type`]='{ item }'>
                    <v-chip x-small :color="item.commission_type === 'PERCENT' ? 'primary' : 'teal'" dark>
                      {{ item.commission_type }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ManagerSellerManagement',

  data: () => ({
    tab: 0,

    sellers: [],
    sellersLoading: false,
    sellerSearch: '',

    commissionType: 'PERCENT',
    commissionValue: 0,
    commissionLoading: false,
    commissionSaving: false,
    commissionUpdatedAt: null,
    commissionExamplePrice: 10000,

    history: [],
    historyTotals: {
      base_amount: '0',
      commission_amount: '0',
      seller_amount: '0',
    },
    historyCount: 0,
    historyLoading: false,
    historySearch: '',
    historyFilters: {
      seller_id: '',
      product_id: '',
      start_date: '',
      end_date: '',
    },

    sellerHeaders: [
      { text: 'Username', value: 'username' },
      { text: 'Email', value: 'email' },
      { text: 'Business', value: 'business_name' },
      { text: 'Balance', value: 'seller_balance' },
      { text: 'Total Earned', value: 'total_earned' },
      { text: 'Products', value: 'product_count' },
      { text: 'Active', value: 'is_active' },
      { text: 'Created', value: 'created_at' },
    ],

    historyHeaders: [
      { text: 'Order ID', value: 'order_id' },
      { text: 'Payment Ref', value: 'payment_ref' },
      { text: 'Source', value: 'source' },
      { text: 'Payment Method', value: 'payment_method' },
      { text: 'Seller', value: 'seller' },
      { text: 'Buyer', value: 'buyer' },
      { text: 'Product', value: 'product' },
      { text: 'Qty', value: 'quantity' },
      { text: 'Unit Price', value: 'unit_price' },
      { text: 'Base', value: 'base_amount' },
      { text: 'Type', value: 'commission_type' },
      { text: 'Value', value: 'commission_value' },
      { text: 'Commission', value: 'commission_amount' },
      { text: 'Seller Net', value: 'seller_amount' },
      { text: 'Date', value: 'created_at' },
    ],
  }),

  computed: {
    sellerTotals() {
      const count = this.sellers.length
      const active = this.sellers.filter(s => s.is_active).length
      const balance = this.sellers.reduce((sum, s) => sum + (parseFloat(s.seller_balance) || 0), 0)
      const earned = this.sellers.reduce((sum, s) => sum + (parseFloat(s.total_earned) || 0), 0)
      return {
        count,
        active,
        balance: Math.round(balance),
        earned: Math.round(earned),
      }
    },

    commissionExampleCommission() {
      const price = parseFloat(this.commissionExamplePrice) || 0
      const value = parseFloat(this.commissionValue) || 0
      if (this.commissionType === 'FIXED') return Math.round(value)
      return Math.round((price * value) / 100)
    },

    commissionExampleNet() {
      const price = parseFloat(this.commissionExamplePrice) || 0
      const net = price - this.commissionExampleCommission
      return net < 0 ? 0 : Math.round(net)
    },
  },

  mounted() {
    document.title = 'Manager | Seller Management'
    this.refreshAll()
  },

  methods: {
    refreshAll() {
      this.loadSellers()
      this.loadCommissionConfig()
      this.loadHistory()
    },

    async loadSellers() {
      this.sellersLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/manager/sellers/')
        this.sellers = res.data
      } catch (err) {
        this.notifyError(err, 'Failed to load sellers.')
      } finally {
        this.sellersLoading = false
      }
    },

    async loadCommissionConfig() {
      this.commissionLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/manager/commission/config/')
        const data = res.data || {}
        this.commissionType = data.commission_type || 'PERCENT'
        this.commissionValue = data.value || 0
        this.commissionUpdatedAt = data.updated_at
      } catch (err) {
        this.notifyError(err, 'Failed to load commission config.')
      } finally {
        this.commissionLoading = false
      }
    },

    async saveCommissionConfig() {
      this.commissionSaving = true
      try {
        const payload = {
          commission_type: this.commissionType,
          value: this.commissionValue,
        }
        const res = await axios.post('/api/v1/market_place/manager/commission/config/', payload)
        this.commissionType = res.data.commission_type
        this.commissionValue = res.data.value
        this.$store.commit('setSnackBarMessage', res.data.message || 'Commission updated')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        this.loadCommissionConfig()
        this.loadHistory()
      } catch (err) {
        this.notifyError(err, 'Failed to update commission config.')
      } finally {
        this.commissionSaving = false
      }
    },

    async loadHistory() {
      this.historyLoading = true
      try {
        const params = { ...this.historyFilters }
        const res = await axios.get('/api/v1/market_place/manager/seller/commission/history/', { params })
        this.history = res.data.results || []
        this.historyTotals = res.data.totals || this.historyTotals
        this.historyCount = res.data.count || 0
      } catch (err) {
        this.notifyError(err, 'Failed to load commission history.')
      } finally {
        this.historyLoading = false
      }
    },

    notifyError(err, fallback) {
      const msg = err.response?.data?.error || err.response?.data?.message || fallback
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },

    sourceColor(source) {
      return {
        deposit: 'indigo',
        wallet: 'teal',
        campaign: 'deep-purple',
      }[source] || 'grey'
    },

    sourceLabel(source) {
      return {
        deposit: 'Deposit',
        wallet: 'Wallet',
        campaign: 'Campaign',
      }[source] || (source || 'Unknown')
    },
  },
}
</script>

<style scoped>
.seller-management-card {
  border-radius: 16px;
}

.summary-card {
  background: #f7f9fc;
  border: 1px solid #e6e9f2;
  border-radius: 12px;
  padding: 14px 16px;
}

.summary-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
</style>
